// @flow

import React, { PureComponent } from 'react';
import { graphql, QueryRenderer } from 'react-relay';
import type { RouterHistory } from 'react-router';
import styled from 'styled-components';
import { i18n } from 'shared/utils';
import relayEnvironment from 'shared/gql/relayEnvironment';
import Loader from 'shared/components/common/Loader';
import Table from 'shared/components/common/Table';
import Page from 'main-app/components/Page';
import PageHeaderList from 'main-app/components/PageHeaderList';
import moment from 'moment';

type Props = {
  history: RouterHistory,
};

type State = {
  triggerFilter: ?string,
};

const FilterSection = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 24px 16px 24px;
`;

const FilterSelect = styled.select`
  padding: 8px 12px;
  border: 1px solid #d0d0d0;
  border-radius: 4px;
  font-size: 14px;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #4a90e2;
  }
`;

const ContentWrapper = styled.div`
  flex: 1;
`;

const EmptyState = styled.div`
  padding: 48px;
  text-align: center;
  color: #666;
  font-size: 16px;
`;

const TriggerBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${props => {
    switch (props.trigger) {
      case 'LOW_STOCK':
        return '#cfe2ff';
      case 'ORDER_REQUIREMENT':
        return '#d1f2dd';
      default:
        return '#e0e0e0';
    }
  }};
  color: ${props => {
    switch (props.trigger) {
      case 'LOW_STOCK':
        return '#084298';
      case 'ORDER_REQUIREMENT':
        return '#0f5132';
      default:
        return '#666';
    }
  }};
`;

const POLink = styled.a`
  color: #4a90e2;
  text-decoration: none;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OrderLink = styled.a`
  color: #4a90e2;
  text-decoration: none;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

class GenerationHistory extends PureComponent<Props, State> {
  state = {
    triggerFilter: null,
  };

  handleFilterChange = (event: SyntheticInputEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    this.setState({
      triggerFilter: value === 'ALL' ? null : value,
    });
  };

  render() {
    const { history } = this.props;
    const { triggerFilter } = this.state;

    return (
      <Page>
        <PageHeaderList
          title={i18n.t('PO Generation History')}
          subtitle={i18n.t('Audit trail of auto-generated purchase orders')}
        />

        <FilterSection>
          <label htmlFor="trigger-filter" style={{ fontSize: 14, color: '#666' }}>
            {i18n.t('Filter by Trigger:')}
          </label>
          <FilterSelect
            id="trigger-filter"
            value={triggerFilter || 'ALL'}
            onChange={this.handleFilterChange}
          >
            <option value="ALL">{i18n.t('All Triggers')}</option>
            <option value="LOW_STOCK">{i18n.t('Low Stock')}</option>
            <option value="ORDER_REQUIREMENT">{i18n.t('Order Requirement')}</option>
          </FilterSelect>
        </FilterSection>

        <ContentWrapper>
          <QueryRenderer
            environment={relayEnvironment}
            query={graphql`
              query GenerationHistoryQuery($triggerType: String) {
                poGenerationLogs(first: 1000, triggerType: $triggerType)
                  @connection(key: "GenerationHistory_poGenerationLogs") {
                  edges {
                    node {
                      id
                      triggerType
                      item {
                        id
                        itemNumber
                        name
                      }
                      order {
                        id
                        orderNumber
                      }
                      purchaseOrder {
                        id
                        poNumber
                        status
                      }
                      createdAt
                    }
                  }
                }
              }
            `}
            variables={{
              triggerType: triggerFilter,
            }}
            render={({ props, error }) => {
              if (error) {
                return (
                  <EmptyState>
                    {i18n.t('Error loading generation history')}
                  </EmptyState>
                );
              }

              if (!props) {
                return <Loader />;
              }

              const logs = props.poGenerationLogs?.edges?.filter(edge => edge && edge.node) || [];

              if (logs.length === 0) {
                return (
                  <EmptyState>
                    {i18n.t('No generation history found')}
                  </EmptyState>
                );
              }

              return (
                <Table
                  columns={[
                    {
                      Header: i18n.t('PO Number'),
                      accessor: 'poNumber',
                      width: 150,
                    },
                    {
                      Header: i18n.t('Trigger Type'),
                      accessor: 'triggerType',
                      width: 150,
                    },
                    {
                      Header: i18n.t('Item'),
                      accessor: 'item',
                      width: 200,
                    },
                    {
                      Header: i18n.t('Source Order'),
                      accessor: 'sourceOrder',
                      width: 150,
                    },
                    {
                      Header: i18n.t('Generated Date'),
                      accessor: 'generatedDate',
                      width: 180,
                    },
                    {
                      Header: i18n.t('Status'),
                      accessor: 'status',
                      width: 120,
                    },
                  ]}
                  data={logs.map(({ node }) => ({
                    id: node.id,
                    poNumber: node.purchaseOrder ? (
                      <POLink
                        onClick={() => history.push(`/purchase-order/${node.purchaseOrder.id}`)}
                      >
                        {node.purchaseOrder.poNumber}
                      </POLink>
                    ) : '-',
                    triggerType: (
                      <TriggerBadge trigger={node.triggerType}>
                        {node.triggerType === 'LOW_STOCK' ? 'Low Stock' : 'Order Requirement'}
                      </TriggerBadge>
                    ),
                    item: node.item ? `${node.item.itemNumber} - ${node.item.name}` : '-',
                    sourceOrder: node.order ? (
                      <OrderLink
                        onClick={() => history.push(`/order/${node.order.id}`)}
                      >
                        {node.order.orderNumber}
                      </OrderLink>
                    ) : '-',
                    generatedDate: moment(node.createdAt).format('MMM DD, YYYY HH:mm'),
                    status: node.purchaseOrder?.status || '-',
                  }))}
                  onRowClick={row => {
                    const log = logs.find(({ node }) => node.id === row.id);
                    if (log?.node?.purchaseOrder) {
                      history.push(`/purchase-order/${log.node.purchaseOrder.id}`);
                    }
                  }}
                />
              );
            }}
          />
        </ContentWrapper>
      </Page>
    );
  }
}

export default GenerationHistory;
